/**
 * SKU exclusion list (#2575). An excluded SKU leaves BOTH the alert
 * evaluator and the tenant 24h series — the same anti-join.
 */
import { useState } from 'react';
import type { CodexCapacityExcludedSku } from '@nadoutong/contracts';
import { useOpsUserLabel } from '../../api/directory.ts';
import { formatLocalTimestamp } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  ConfirmDialog,
  FormField,
  Input,
  Table,
  TableEmpty,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';

interface Props {
  readonly excluded: readonly CodexCapacityExcludedSku[];
  readonly adding: boolean;
  readonly removingSku: string | null;
  readonly onAdd: (skuName: string) => void;
  readonly onRemove: (skuName: string) => void;
}

export function CodexCapacityExcludedSkusPanel({
  excluded,
  adding,
  removingSku,
  onAdd,
  onRemove,
}: Props): JSX.Element {
  const t = useT();
  const opsUserLabel = useOpsUserLabel();
  const [skuName, setSkuName] = useState('');
  const [pendingAdd, setPendingAdd] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);
  const trimmed = skuName.trim();

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-dense text-paper-mid" data-testid="codex-capacity-excluded-semantics">
        {t('codex_capacity.excluded.semantics')}
      </p>

      <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
        <FormField label={t('codex_capacity.excluded.sku')} fieldId="codex-capacity-excluded-sku">
          <Input
            id="codex-capacity-excluded-sku"
            value={skuName}
            onChange={(event) => setSkuName(event.currentTarget.value)}
            data-testid="codex-capacity-excluded-sku"
          />
        </FormField>
        <Button
          disabled={trimmed.length === 0}
          onClick={() => setPendingAdd(true)}
          data-testid="codex-capacity-excluded-add"
        >
          {t('codex_capacity.excluded.add')}
        </Button>
      </div>

      <Table testId="codex-capacity-excluded-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('codex_capacity.excluded.column.sku')}</Th>
            <Th>{t('codex_capacity.excluded.column.created')}</Th>
            <Th>{t('codex_capacity.excluded.column.by')}</Th>
            <Th>{t('codex_capacity.column.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {excluded.length === 0 ? (
            <TableEmpty colSpan={4}>{t('codex_capacity.excluded.empty')}</TableEmpty>
          ) : (
            excluded.map((row) => (
              <Tr
                key={row.sku_name}
                interactive={false}
                data-testid={`codex-capacity-excluded-row-${row.sku_name}`}
              >
                <Td mono>{row.sku_name}</Td>
                <Td mono>{formatLocalTimestamp(row.created_at, { seconds: true })}</Td>
                <Td>
                  {row.created_by_ops_user_id === null
                    ? '—'
                    : opsUserLabel(row.created_by_ops_user_id) || row.created_by_ops_user_id}
                </Td>
                <Td>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={removingSku === row.sku_name}
                    onClick={() => setPendingRemove(row.sku_name)}
                    data-testid={`codex-capacity-excluded-remove-${row.sku_name}`}
                  >
                    {t('codex_capacity.excluded.remove')}
                  </Button>
                </Td>
              </Tr>
            ))
          )}
        </TBody>
      </Table>

      <ConfirmDialog
        open={pendingAdd}
        onOpenChange={(open) => {
          if (!open) setPendingAdd(false);
        }}
        tone="danger"
        title={t('codex_capacity.excluded.confirm_title', { sku: trimmed })}
        body={
          <p
            className="m-0 rounded border border-bad-line bg-bad-dim/50 px-2.5 py-2 text-dense text-bad-bright"
            data-testid="codex-capacity-excluded-confirm-consequence"
          >
            {t('codex_capacity.excluded.confirm_consequence')}
          </p>
        }
        confirmLabel={t('codex_capacity.excluded.confirm_action')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={adding}
        onConfirm={() => {
          if (trimmed.length === 0) return;
          onAdd(trimmed);
          setPendingAdd(false);
          setSkuName('');
        }}
        testId="codex-capacity-excluded-confirm"
      />

      <ConfirmDialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemove(null);
        }}
        title={t('codex_capacity.excluded.remove_title', { sku: pendingRemove ?? '' })}
        body={
          <p
            className="m-0 rounded border border-warn-line bg-warn-dim/50 px-2.5 py-2 text-dense text-warn"
            data-testid="codex-capacity-excluded-remove-confirm-consequence"
          >
            {t('codex_capacity.excluded.remove_consequence')}
          </p>
        }
        confirmLabel={t('codex_capacity.excluded.remove_action')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={removingSku !== null}
        onConfirm={() => {
          if (pendingRemove === null) return;
          onRemove(pendingRemove);
          setPendingRemove(null);
        }}
        testId="codex-capacity-excluded-remove-confirm"
      />
    </div>
  );
}
